<script setup lang="ts">
import type { SmsPricing, Upsell } from '../types/types'
import type { dropdownValues } from '../types/dropdown'

const { t } = useI18n()
const { stores } = useLocalStorage()

const props = defineProps<{
  smsPricing?: SmsPricing[]
  pricing?: number
}>()

const isSmsComponent = computed(() => props.smsPricing?.length > 0)

const upsellInfos = ref<Upsell[]>([
  {
    store: stores.value[0].name,
    storeId: stores.value[0].id,
    ...(isSmsComponent.value && { packSms: props.smsPricing[0].smsCount }),
    ...(isSmsComponent.value && { packSmsId: 0 }),
    price: isSmsComponent.value ? props.smsPricing[0].pricePerMonth : props.pricing
  }
])

const updateStore = (upsellInfo: Upsell, storesValues: dropdownValues[], id: number) => {
  upsellInfo.store = storesValues.find((store) => store.id === id).text
  upsellInfo.storeId = id
}

const storesValues = computed<dropdownValues[]>(() => {
  return stores.value.map((store: any) => {
    return {
      id: store.id,
      text: store.name,
      disabled: upsellInfos.value.some((upsellInfo: Upsell) => upsellInfo.storeId === store.id)
    }
  })
})

const smsPricingValues = computed(() => {
  return props.smsPricing.map((sms: SmsPricing, index) => {
    return {
      id: index,
      text: sms.smsCount.toString()
    }
  })
})

const updatePackSms = (upsellInfo: Upsell, id: number) => {
  upsellInfo.packSms = props.smsPricing[id].smsCount
  upsellInfo.price = props.smsPricing[id].pricePerMonth
  upsellInfo.packSmsId = id
  emit('onUpdateModal', upsellInfos.value)
}

const add = () => {
  if (upsellInfos.value.length < stores.value.length) {
    const availableStores = storesValues.value.filter((store) => !store.disabled)

    if (availableStores.length > 0) {
      upsellInfos.value.push({
        store: availableStores[0].text,
        storeId: availableStores[0].id,
        ...(isSmsComponent.value && { packSms: props.smsPricing[0].smsCount }),
        ...(isSmsComponent.value && { packSmsId: 0 }),
        price: isSmsComponent.value ? props.smsPricing[0].pricePerMonth : props.pricing
      })
      emit('onUpdateModal', upsellInfos.value)
    }
  }
}

const remove = (value: number) => {
  if (upsellInfos.value.length > 1) {
    upsellInfos.value.splice(value, 1)
    emit('onUpdateModal', upsellInfos.value)
  }
}

const emit = defineEmits<{
  (e: 'onUpdateModal', data: Upsell[]): void
}>()

onMounted(() => {
  emit('onUpdateModal', upsellInfos.value)
})
</script>
<template>
  <div class="flex flex-col gap-2 border-b px-5 py-3">
    <div class="flex w-full justify-between">
      <p class="text-sm font-extrabold">{{ t('upsell.modal.store') }}</p>
      <p v-if="isSmsComponent" class="ml-6 text-sm font-extrabold">{{ t('upsell.modal.pack') }}</p>
      <p class="text-sm font-extrabold">{{ t('upsell.modal.price') }}</p>
    </div>
    <div
      v-for="(upsellInfo, indexUpsell) in upsellInfos"
      :key="upsellInfo.storeId"
      class="flex w-full items-center gap-2"
    >
      <div class="flex w-full items-center justify-between">
        <div class="flex items-start justify-between gap-2">
          <ComboboxSelect
            value-key="id"
            label-key="text"
            trigger-class="w-44"
            :placeholder="upsellInfo.store"
            :model-value="upsellInfo.storeId"
            :options="storesValues"
            @update:model-value="updateStore(upsellInfo, storesValues, $event)"
          />
          <div v-if="isSmsComponent" class="ml-6 flex flex-col items-center justify-between">
            <ComboboxSelect
              value-key="id"
              label-key="text"
              trigger-class="w-24"
              :placeholder="upsellInfo.packSms.toString()"
              :model-value="upsellInfo.packSmsId"
              :options="smsPricingValues"
              @update:model-value="updatePackSms(upsellInfo, $event)"
            />
          </div>
        </div>
        <div class="flex flex-col items-end justify-between">
          <p>{{ upsellInfo.price }}</p>
        </div>
      </div>
      <div v-if="upsellInfos.length > 1" class="cursor-pointer" @click="remove(indexUpsell)">
        <BaseIcon name="HeroTrashIconSolid" class="h-6 w-6 rounded-full border bg-white p-0.5" />
      </div>
    </div>

    <div
      v-if="upsellInfos.length < stores.length"
      class="col-span-2 my-4 flex w-full cursor-pointer items-center gap-2"
      @click="add"
    >
      <BaseIcon name="HeroPlusIconSolid" class="h-5 w-5 rounded-full border bg-white p-0.5" />
      <p class="text-sm">{{ t('upsell.modal.add') }}</p>
    </div>
  </div>
</template>
